import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { HttpError } from '@shared/services';

import { authService } from '../services';
import { useAuthStore } from '../store';
import type { AuthUser, LoginInput } from '../validations';

export interface UseAuthReturn {
  readonly user: AuthUser | null;
  readonly isAuthenticated: boolean;
  readonly isPending: boolean;
  readonly authError: string | undefined;
  readonly login: (input: LoginInput) => void;
  readonly logout: () => void;
  readonly resetAuthError: () => void;
}

export function useAuth(): UseAuthReturn {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const setSession = useAuthStore((s) => s.setSession);
  const clearSession = useAuthStore((s) => s.clearSession);
  const refreshToken = useAuthStore((s) => s.refreshToken);

  const loginMutation = useMutation({
    mutationFn: async (input: LoginInput) => authService.login(input),
    onSuccess: (response) => {
      setSession({
        user: response.user,
        accessToken: response.accessToken,
        refreshToken: response.refreshToken,
      });
      void navigate('/dashboard', { replace: true });
    },
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onError: () => {},
  });

  const logoutMutation = useMutation({
    mutationFn: async () => authService.logout(refreshToken ?? undefined),
    onSuccess: () => {
      clearSession();
      toast.success('Sessão encerrada');
      void navigate('/login', { replace: true });
    },
    onError: () => {
      clearSession();
      void navigate('/login', { replace: true });
    },
  });

  const login = useCallback(
    (input: LoginInput) => {
      loginMutation.mutate(input);
    },
    [loginMutation],
  );

  const logout = useCallback(() => {
    logoutMutation.mutate();
  }, [logoutMutation]);

  const resetAuthError = useCallback(() => {
    loginMutation.reset();
  }, [loginMutation]);

  return {
    user,
    isAuthenticated: user !== null,
    isPending: loginMutation.isPending || logoutMutation.isPending,
    authError: loginMutation.error !== null ? mapAuthError(loginMutation.error) : undefined,
    login,
    logout,
    resetAuthError,
  };
}

function mapAuthError(error: unknown): string {
  if (HttpError.isHttpError(error)) {
    switch (error.statusCode) {
      case 401:
        return 'Usuário e senha incorretos.';
      case 403:
        return 'Acesso negado.';
      case 429:
        return 'Muitas tentativas. Aguarde alguns minutos antes de tentar novamente.';
      default:
        return 'Não foi possível entrar agora. Tente novamente em instantes.';
    }
  }

  const axiosError = error as { response?: { status?: number } };
  if (axiosError?.response?.status !== undefined) {
    switch (axiosError.response.status) {
      case 401:
        return 'Usuário e senha incorretos.';
      case 403:
        return 'Acesso negado.';
      case 429:
        return 'Muitas tentativas. Aguarde alguns minutos antes de tentar novamente.';
      default:
        return 'Não foi possível entrar agora. Tente novamente em instantes.';
    }
  }

  return 'Não foi possível entrar agora. Tente novamente em instantes.';
}
